import { V2Button, V2FormField, V2ModalForm, V2Spinner } from "@cakewalk/ui/v2";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

/**
 * Collects the employer's BambooHR subdomain before the OAuth popup opens.
 *
 * QuickBooks needs nothing up front — Intuit's authorize URL is global. BambooHR
 * is per-tenant on BOTH its authorize and token URLs
 * (`https://{subdomain}.bamboohr.com/...`), so the server cannot mint the OAuth
 * URL without this value. It then rides in the HMAC-signed `state` so the public
 * callback can rebuild the token URL with no session.
 */

/**
 * Client-side shape. A structural subset of the API's
 * `CensusBambooHrAuthorizeRequestSchema` (`{ companyDomain: string }`), tightened
 * here per the forms rule — the SPA may narrow the API's constraints, never
 * loosen them. `apps/company-census/src/forms/bamboohr-domain.compat.test.ts`
 * proves the subset relationship.
 *
 * Kept permissive enough to accept a pasted URL: the server's
 * `normalizeCompanyDomain` is the single authority on what may reach the host
 * position, and duplicating its exact regex here would just be two places to
 * keep in sync.
 */
export const bambooHrDomainSchema = z.object({
  companyDomain: z
    .string()
    .min(1, "Enter your BambooHR domain")
    .max(255)
    .refine((v) => /[a-zA-Z0-9]/.test(v), "Enter your BambooHR domain"),
});

export type BambooHrDomainInput = z.infer<typeof bambooHrDomainSchema>;

export function BambooHRDomainModal({
  open,
  onOpenChange,
  onSubmitDomain,
  isBusy = false,
  error,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the raw entry; the server normalizes and validates it. */
  onSubmitDomain: (companyDomain: string) => void;
  /** True while the authorize call is in flight. */
  isBusy?: boolean;
  /** Server-side failure, surfaced at form level. */
  error?: string | null;
}) {
  const form = useForm<BambooHrDomainInput>({
    resolver: zodResolver(bambooHrDomainSchema),
    defaultValues: { companyDomain: "" },
  });

  const onSubmit = form.handleSubmit((values) => {
    onSubmitDomain(values.companyDomain);
  });

  const submitting = form.formState.isSubmitting || isBusy;

  return (
    <FormProvider {...form}>
      {/* `open` is a real boolean and the modal stays mounted, so Radix can play
          its exit animation — see the overlay-lifecycle rule. */}
      <V2ModalForm
        open={open}
        onOpenChange={(next) => {
          if (!next) form.reset();
          onOpenChange(next);
        }}
        onSubmit={onSubmit}
        title="Connect BambooHR"
        description="We'll ask BambooHR for permission, then bring your team over. You review everything before anything goes live."
        size="sm"
        secondaryAction={
          <V2Button
            type="button"
            variant="link"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
            data-track="button-company-census-bamboohr-domain-cancel"
          >
            Cancel
          </V2Button>
        }
        primaryAction={
          <V2Button
            type="submit"
            variant="primary"
            disabled={submitting}
            data-track="button-company-census-bamboohr-domain-submit"
          >
            {submitting ? (
              <>
                <V2Spinner size="sm" />
                Opening BambooHR…
              </>
            ) : (
              "Continue to BambooHR"
            )}
          </V2Button>
        }
      >
        <V2FormField name="companyDomain" required>
          <V2FormField.Label>Your BambooHR domain</V2FormField.Label>
          <V2FormField.Input
            placeholder="your-company"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            // No `data-sensitive` needed: the shared PostHog config sets
            // `maskAllInputs: true`, so this value is masked in session replay
            // regardless. `data-sensitive` only adds text-node masking, which a
            // company subdomain does not require.
          />
          <V2FormField.Hint>
            The part before <code>.bamboohr.com</code> in your BambooHR web address. Pasting the
            whole URL works too.
          </V2FormField.Hint>
          <V2FormField.Error />
        </V2FormField>

        {error ? (
          <p role="alert" className="mt-3 text-cw-v2-reliant-red">
            {error}
          </p>
        ) : null}
      </V2ModalForm>
    </FormProvider>
  );
}
